// @flow

import * as ACTIONS from '../constants/actions';
import {
  tags as tagsAPI,
  locationTags as locationTagsAPI,
  locations as locationsAPI,
} from '../services/api';
import { errorToast, successToast } from '../utils/toaster';
import {
  getAuthPkgUser,
  initAuthPkgUser,
} from '../services/auth-pkg-user';

const NOT_SET = 'NOT_SET';

const orgIdFromState = (state: any): ?string => {
  const id =
    state && state.user && state.user.model && state.user.model.organisationId;
  if (typeof id !== 'string' || id.length === 0 || id === NOT_SET) return null;
  return id;
};

// Resolves the caller's primary org. Prefers redux state (populated by the
// auth flow before any thunk runs); falls back to auth-pkg only if redux
// state isn't available yet. auth-pkg's getPrimaryOrgId() can return the
// literal 'NOT_SET' if the bootstrap hasn't finished loading, which we
// explicitly reject so callers don't pass that to the API.
const resolveOrgId = async (getState?: () => any): Promise<?string> => {
  if (getState) {
    const fromState = orgIdFromState(getState());
    if (fromState) return fromState;
  }
  let authUser = getAuthPkgUser();
  if (!authUser) {
    try {
      authUser = await initAuthPkgUser();
    } catch (e) {
      return null;
    }
  }
  if (!authUser) return null;
  try {
    const id = await authUser.getPrimaryOrgId();
    if (typeof id !== 'string' || id === NOT_SET) return null;
    return id;
  } catch (e) {
    return null;
  }
};

export const fetchTags =
  () => async (dispatch: (any) => void, getState: () => any) => {
  try {
    dispatch({ type: ACTIONS.FETCH_TAGS_REQUEST });
    const orgId = await resolveOrgId(getState);
    if (!orgId) throw new Error('No primary org');
    const tagList = await tagsAPI.list(orgId);
    dispatch({
      type: ACTIONS.FETCH_TAGS_SUCCESS,
      payload: Array.isArray(tagList) ? tagList : [],
    });
  } catch (err) {
    dispatch({
      type: ACTIONS.FETCH_TAGS_ERROR,
      payload: err && err.message ? err.message : 'unknown',
    });
    errorToast({ message: 'Error loading tags.', timeout: 2500 });
  }
};

export const createTag =
  (tagType: string, tagValue: string) =>
  async (dispatch: (any) => void, getState: () => any) => {
    try {
      const orgId = await resolveOrgId(getState);
      if (!orgId) throw new Error('No primary org');
      const tag = await tagsAPI.create({
        organisation: orgId,
        targetCollection: 'locations',
        tagType,
        tagValue,
      });
      dispatch({ type: ACTIONS.CREATE_TAG_SUCCESS, payload: tag });
      successToast({
        message: `Added ${tag.tagType}: ${tag.tagValue}`,
        timeout: 2000,
      });
      return tag;
    } catch (err) {
      const status = err && err.response && err.response.status;
      if (status === 409) {
        errorToast({ message: 'That tag already exists.', timeout: 2500 });
      } else if (status === 403) {
        errorToast({
          message: 'You do not have permission to create tags.',
          timeout: 2500,
        });
      } else {
        errorToast({ message: 'Error creating tag.', timeout: 2500 });
      }
      return null;
    }
  };

export const updateTag =
  (tagId: string, tagValue: string) => async (dispatch: (any) => void) => {
    try {
      const tag = await tagsAPI.update(tagId, { tagValue });
      dispatch({ type: ACTIONS.UPDATE_TAG_SUCCESS, payload: tag });
      successToast({ message: 'Tag updated', timeout: 2000 });
      return tag;
    } catch (err) {
      const status = err && err.response && err.response.status;
      if (status === 409) {
        errorToast({
          message: 'Another tag with that value already exists.',
          timeout: 2500,
        });
      } else if (status === 403) {
        errorToast({
          message: 'You do not have permission to rename tags.',
          timeout: 2500,
        });
      } else if (status === 404) {
        errorToast({ message: 'That tag no longer exists.', timeout: 2500 });
        dispatch({ type: ACTIONS.DELETE_TAG_SUCCESS, payload: { id: tagId } });
      } else {
        errorToast({ message: 'Error updating tag.', timeout: 2500 });
      }
      return null;
    }
  };

export const deleteTag = (tagId: string) => async (dispatch: (any) => void) => {
  try {
    const result = await tagsAPI.delete(tagId);
    dispatch({ type: ACTIONS.DELETE_TAG_SUCCESS, payload: { id: tagId } });
    const affected = (result && result.locationsAffected) || 0;
    successToast({
      message:
        affected > 0
          ? `Tag deleted · unassigned from ${affected} location${affected === 1 ? '' : 's'}`
          : 'Tag deleted',
      timeout: 3000,
    });
    return true;
  } catch (err) {
    const status = err && err.response && err.response.status;
    if (status === 404) {
      dispatch({ type: ACTIONS.DELETE_TAG_SUCCESS, payload: { id: tagId } });
      return true;
    }
    if (status === 502) {
      errorToast({
        message:
          'Could not finalise tag deletion. Please retry — no changes were made.',
        timeout: 4000,
      });
      dispatch(fetchTags());
      return false;
    }
    errorToast({ message: 'Error deleting tag.', timeout: 2500 });
    return false;
  }
};

export const assignTagToLocation =
  (locationId: string, newTag: { id: string, tagType: string, tagValue: string }) =>
  async (dispatch: (any) => void, getState: () => any) => {
    const state = getState();
    const loc = (state.locations || []).find((l) => l.id === locationId);
    const tagsById = (state.entityTags && state.entityTags.byId) || {};

    const conflict = ((loc && loc.tags) || [])
      .map((id) => tagsById[id])
      .filter(Boolean)
      .find((t) => t.tagType === newTag.tagType && t.id !== newTag.id);

    try {
      if (conflict) {
        await locationTagsAPI.unassign(locationId, conflict.id);
        dispatch({
          type: ACTIONS.REMOVE_TAG_FROM_LOCATION_SUCCESS,
          payload: { locationId, tagId: conflict.id },
        });
      }
      await locationTagsAPI.assign(locationId, newTag.id);
      dispatch({
        type: ACTIONS.ASSIGN_TAG_TO_LOCATION_SUCCESS,
        payload: { locationId, tagId: newTag.id },
      });
      successToast({
        message: `Added ${newTag.tagType}: ${newTag.tagValue}`,
        timeout: 2000,
      });
      return true;
    } catch (err) {
      const status = err && err.response && err.response.status;
      if (status === 409) {
        // Already assigned — treat as success
        dispatch({
          type: ACTIONS.ASSIGN_TAG_TO_LOCATION_SUCCESS,
          payload: { locationId, tagId: newTag.id },
        });
        return true;
      }
      if (status === 403) {
        errorToast({
          message: 'You do not have permission to tag this location.',
          timeout: 2500,
        });
      } else if (status === 400) {
        errorToast({
          message: 'Could not tag this location. Please refresh and try again.',
          timeout: 2500,
        });
      } else {
        errorToast({ message: 'Error tagging location.', timeout: 2500 });
      }
      return false;
    }
  };

export const removeTagFromLocation =
  (locationId: string, tagId: string) => async (dispatch: (any) => void) => {
    try {
      await locationTagsAPI.unassign(locationId, tagId);
      dispatch({
        type: ACTIONS.REMOVE_TAG_FROM_LOCATION_SUCCESS,
        payload: { locationId, tagId },
      });
      return true;
    } catch (err) {
      const status = err && err.response && err.response.status;
      if (status === 404) {
        // Already removed
        dispatch({
          type: ACTIONS.REMOVE_TAG_FROM_LOCATION_SUCCESS,
          payload: { locationId, tagId },
        });
        return true;
      }
      if (status === 403) {
        errorToast({
          message: 'You do not have permission to remove tags.',
          timeout: 2500,
        });
      } else {
        errorToast({ message: 'Error removing tag.', timeout: 2500 });
      }
      return false;
    }
  };

const groupBy = <T>(items: T[], keyFn: (T) => ?string): { [string]: T[] } => {
  const out = {};
  items.forEach((it) => {
    const k = keyFn(it);
    if (k == null) return;
    (out[k] = out[k] || []).push(it);
  });
  return out;
};

export const bulkAssignTags =
  (locationIds: string[], pickedTag: { id: string, tagType: string, tagValue: string }) =>
  async (dispatch: (any) => void, getState: () => any) => {
    if (!locationIds || locationIds.length === 0) return false;
    const state = getState();
    const tagsById = (state.entityTags && state.entityTags.byId) || {};
    const allLocations = state.locations || [];

    // Compute same-type tag IDs to remove first, grouped by tagId
    const replaceLocations = locationIds
      .map((id) => allLocations.find((l) => l.id === id))
      .filter(Boolean)
      .map((l) => {
        const sameTypeTagId = (l.tags || [])
          .map((tid) => tagsById[tid])
          .filter(Boolean)
          .filter((t) => t.tagType === pickedTag.tagType && t.id !== pickedTag.id)
          .map((t) => t.id)[0];
        return sameTypeTagId ? { locationId: l.id, sameTypeTagId } : null;
      })
      .filter(Boolean);

    const groups = groupBy(replaceLocations, (r) => r.sameTypeTagId);

    let totalErrors = 0;
    let totalReplaced = 0;

    try {
      for (const tagIdToRemove of Object.keys(groups)) {
        const locs = groups[tagIdToRemove];
        const res = await locationTagsAPI.bulkRemove(
          locs.map((r) => r.locationId),
          [tagIdToRemove],
        );
        const results = (res && res.results) || [];
        const errors = (res && res.errors) || [];
        totalErrors += errors.length;
        totalReplaced += results.reduce(
          (acc, r) => acc + ((r.removed && r.removed.length) || 0),
          0,
        );
        dispatch({ type: ACTIONS.BULK_REMOVE_TAGS_SUCCESS, payload: res });
      }

      const assignRes = await locationTagsAPI.bulkAssign(locationIds, [
        pickedTag.id,
      ]);
      const aErrors = (assignRes && assignRes.errors) || [];
      const aResults = (assignRes && assignRes.results) || [];
      totalErrors += aErrors.length;
      const addedCount = aResults.filter(
        (r) => r.added && r.added.length > 0,
      ).length;
      dispatch({ type: ACTIONS.BULK_ASSIGN_TAGS_SUCCESS, payload: assignRes });

      if (totalErrors > 0) {
        errorToast({
          message: `Updated ${addedCount} locations · ${totalErrors} failed; data has been refreshed.`,
          timeout: 3500,
        });
        dispatch(hydrateLocationTags());
      } else if (totalReplaced > 0) {
        successToast({
          message: `Added ${pickedTag.tagType}: ${pickedTag.tagValue} to ${addedCount} locations · replaced existing ${pickedTag.tagType} tag on ${totalReplaced} of them`,
          timeout: 3500,
        });
      } else {
        successToast({
          message: `Added ${pickedTag.tagType}: ${pickedTag.tagValue} to ${addedCount} locations`,
          timeout: 2500,
        });
      }
      return true;
    } catch (err) {
      const status = err && err.response && err.response.status;
      if (status === 403) {
        errorToast({
          message: 'You do not have permission to update tags on these locations.',
          timeout: 2500,
        });
      } else {
        errorToast({ message: 'Bulk update failed.', timeout: 2500 });
        dispatch(hydrateLocationTags());
      }
      return false;
    }
  };

export const bulkRemoveTags =
  (locationIds: string[], pickedTag: { id: string, tagType: string, tagValue: string }) =>
  async (dispatch: (any) => void) => {
    if (!locationIds || locationIds.length === 0) return false;
    try {
      const res = await locationTagsAPI.bulkRemove(locationIds, [pickedTag.id]);
      const errors = (res && res.errors) || [];
      const results = (res && res.results) || [];
      const removedCount = results.filter(
        (r) => r.removed && r.removed.length > 0,
      ).length;
      dispatch({ type: ACTIONS.BULK_REMOVE_TAGS_SUCCESS, payload: res });
      if (errors.length > 0) {
        errorToast({
          message: `Updated ${removedCount} locations · ${errors.length} failed; data has been refreshed.`,
          timeout: 3500,
        });
        dispatch(hydrateLocationTags());
      } else {
        successToast({
          message: `Removed ${pickedTag.tagType}: ${pickedTag.tagValue} from ${removedCount} locations`,
          timeout: 2500,
        });
      }
      return true;
    } catch (err) {
      const status = err && err.response && err.response.status;
      if (status === 403) {
        errorToast({
          message: 'You do not have permission to update tags on these locations.',
          timeout: 2500,
        });
      } else {
        errorToast({ message: 'Bulk update failed.', timeout: 2500 });
        dispatch(hydrateLocationTags());
      }
      return false;
    }
  };

export const hydrateLocationTags =
  () => async (dispatch: (any) => void, getState: () => any) => {
    try {
      const orgId = await resolveOrgId(getState);
      if (!orgId) return;
      const list = await locationsAPI.list(orgId);
      const safeList = Array.isArray(list) ? list : [];
      const tagsByLocationId = {};
      const allTags = [];
      const seen = {};
      safeList.forEach((l) => {
        if (!l || !l.id) return;
        const tagObjects = Array.isArray(l.tags) ? l.tags : [];
        tagsByLocationId[l.id] = tagObjects
          .map((t) => (t && t.id ? t.id : null))
          .filter(Boolean);
        tagObjects.forEach((t) => {
          if (t && t.id && !seen[t.id]) {
            seen[t.id] = true;
            allTags.push(t);
          }
        });
      });
      dispatch({
        type: ACTIONS.HYDRATE_LOCATION_TAGS,
        payload: { tagsByLocationId },
      });
      dispatch({ type: ACTIONS.FETCH_TAGS_SUCCESS, payload: allTags });
    } catch (err) {
      console.error('Could not load location tags', err);
      errorToast({
        message:
          'Could not load location tags. Tagging features may be unavailable.',
        timeout: 3000,
      });
    }
  };
